/**
 * Currency calculation utilities to handle floating-point precision issues
 */

/**
 * Convert a number to cents (multiply by 100) to avoid floating-point issues
 * @param {number} amount - Amount in dollars
 * @returns {number} Amount in cents
 */
export const toCents = (amount) => {
  return Math.round(amount * 100);
};

/**
 * Convert cents back to dollars (divide by 100)
 * @param {number} cents - Amount in cents
 * @returns {number} Amount in dollars
 */
export const fromCents = (cents) => {
  return cents / 100;
};

/**
 * Add multiple currency amounts together without floating-point errors
 * @param {...number} amounts - Currency amounts to add
 * @returns {number} Sum of amounts in dollars
 */
export const addCurrency = (...amounts) => {
  const totalCents = amounts.reduce((sum, amount) => {
    const cents = toCents(amount || 0);
    return sum + cents;
  }, 0);
  return fromCents(totalCents);
};

/**
 * Multiply a currency amount by a number without floating-point errors
 * @param {number} amount - Currency amount
 * @param {number} multiplier - Number to multiply by
 * @returns {number} Result in dollars
 */
export const multiplyCurrency = (amount, multiplier) => {
  const cents = toCents(amount || 0);
  const resultCents = Math.round(cents * multiplier);
  return fromCents(resultCents);
};

/**
 * Round a currency amount to 2 decimal places
 * @param {number} amount - Currency amount
 * @returns {number} Rounded amount
 */
export const roundCurrency = (amount) => {
  return Math.round(amount * 100) / 100;
};

/**
 * Check if two currency amounts are equal (within 1 cent tolerance)
 * @param {number} amount1 - First amount
 * @param {number} amount2 - Second amount
 * @returns {boolean} True if amounts are equal
 */
export const currencyEquals = (amount1, amount2) => {
  return Math.abs(amount1 - amount2) < 0.01;
};
